import { theme } from '@mds/ui';
import { themeVars } from '@mds/ui/theme/themeVars';
import { styled, useColorScheme } from '@mui/material';
import { colorSchemeDarkBlue, iconSetMaterial, themeQuartz, WithParamTypes } from 'ag-grid-community';
import { useMemo } from 'react';

const light = theme.colorSchemes.light?.palette;
const dark = theme.colorSchemes.dark?.palette;

if (!light || !dark) {
  throw new Error('Missing theme palette');
}

export const gridClasses = {
  cellNumeric: 'mds-ag-cell-numeric',
  cellNoPadding: 'mds-ag-cell-no-padding',
  cellPositive: 'mds-ag-cell-positive',
  cellNegative: 'mds-ag-cell-negative',
  cellWarning: 'mds-ag-cell-warning',
  callInfo: 'mds-ag-call-info',
  headerCellCentered: 'mds-ag-header-cell-centered',
};

/**
 * Regular variant with 40px rows
 */
export const mdsThemeLight = themeQuartz
  .withParams({
    spacing: 8,
    rowVerticalPaddingScale: 0.85,
    headerVerticalPaddingScale: 0.7,
    fontSize: theme.typography.caption.fontSize,

    rowBorder: false,
    borderRadius: theme.shape.borderRadius,
    wrapperBorder: false,
    wrapperBorderRadius: 0,
    fontFamily: theme.typography.body1.fontFamily,
    headerFontWeight: theme.typography.body1.fontWeight,
    iconButtonBackgroundSpread: 2,
    iconButtonBorderRadius: 0,
    widgetContainerHorizontalPadding: 8,
    widgetContainerVerticalPadding: 8,
    widgetHorizontalSpacing: 8,
    widgetVerticalSpacing: 8,
    popupShadow: false,
    statusBarLabelFontWeight: theme.typography.body1.fontWeight,
    statusBarValueFontWeight: theme.typography.body1.fontWeight,
    cellEditingShadow: false,

    backgroundColor: light.background.default,
    foregroundColor: light.text.primary,
    accentColor: light.secondary.main,
    headerTextColor: light.text.secondary,
    iconColor: light.text.secondary,
    headerBackgroundColor: light.background.level1,
    oddRowBackgroundColor: light.background.level1,
    focusShadow: `inset 0 0 0 1px ${light.border.focused}`,
    rangeSelectionBorderColor: light.border.focused,
    cellEditingBorder: `solid 1.5px ${light.border.focused}`,
    rowHoverColor: '#d8d8e0',
    menuBackgroundColor: light.background.level2,
    menuBorder: `solid 1px ${light.border.subtle}`,
    menuSeparatorColor: light.border.subtle,
    statusBarLabelColor: light.text.secondary,
    statusBarValueColor: light.text.secondary,
  })
  .withPart(iconSetMaterial)
  .withParams({
    iconSize: 18,
  });

export const mdsThemeDark = mdsThemeLight.withPart(colorSchemeDarkBlue).withParams({
  backgroundColor: dark.background.default,
  foregroundColor: dark.text.primary,
  accentColor: dark.secondary.main,
  headerTextColor: dark.text.secondary,
  iconColor: dark.text.secondary,
  headerBackgroundColor: dark.background.level1,
  oddRowBackgroundColor: dark.background.level1,
  focusShadow: `inset 0 0 0 1px ${dark.border.focused}`,
  rangeSelectionBorderColor: dark.border.focused,
  cellEditingBorder: `solid 1px ${dark.border.focused}`,
  rowHoverColor: '#28283c',
  popupShadow: false,
  menuBackgroundColor: dark.background.level2,
  menuBorder: `solid 1px ${dark.border.subtle}`,
  menuSeparatorColor: dark.border.subtle,
  menuShadow: false,
  statusBarLabelColor: dark.text.secondary,
  statusBarValueColor: dark.text.secondary,
});

/**
 * 48px rows
 */
export const spaciousDensityParams: WithParamTypes<unknown> = {
  spacing: 12,
  rowVerticalPaddingScale: 0.77,
  headerVerticalPaddingScale: 0.62,
  fontSize: theme.typography.body2.fontSize,
};

/**
 * 32px rows
 */
export const compactDensityParams: WithParamTypes<unknown> = {
  spacing: 8,
  rowVerticalPaddingScale: 0.55,
  headerVerticalPaddingScale: 0.4,
  fontSize: theme.typography.caption.fontSize,
};

const agInputFieldSelector = [
  'input.ag-input-field-input:not([type])',
  'input.ag-input-field-input[type=text]',
  'input.ag-input-field-input[type=number],input.ag-input-field-input[type=tel]',
  'input.ag-input-field-input[type=date]',
  'input.ag-input-field-input[type=datetime-local]',
  'textarea.ag-input-field-input',
].join(', ');

const agFocusedCellSelector = [
  '.ag-cell-focus:not(.ag-cell-range-selected):focus-within',
  '.ag-cell-range-single-cell',
  '.ag-cell-range-single-cell.ag-cell-range-handle',
  '.ag-context-menu-open .ag-cell-focus:not(.ag-cell-range-selected)',
  '.ag-context-menu-open .ag-full-width-row.ag-row-focus .ag-cell-wrapper.ag-row-group',
  '.ag-full-width-row.ag-row-focus:focus .ag-cell-wrapper.ag-row-group',
].join(',');

/**
 * CSS for Marex AgGrid theme
 */
export const StyledAgGridContainer = styled('div')(({ theme }) => ({
  display: 'contents',
  '--ag-checkbox-checked-background-color': themeVars(theme).palette.action.selectedContrast,
  '--ag-checkbox-checked-border-color': themeVars(theme).palette.action.selectedContrast,
  '--ag-checkbox-indeterminate-background-color': themeVars(theme).palette.action.selectedContrast,
  '--ag-checkbox-indeterminate-border-color': themeVars(theme).palette.action.selectedContrast,
  '--ag-checkbox-unchecked-background-color': 'transparent',
  '--ag-checkbox-unchecked-border-color': themeVars(theme).palette.text.secondary,
  '--ag-input-background-color': themeVars(theme).palette.action.enabled,
  '--ag-input-focus-background-color': themeVars(theme).palette.action.enabled,

  '.ag-header-cell-text': {
    textTransform: 'uppercase',
    letterSpacing: theme.typography.overline.letterSpacing,
  },
  '.ag-menu-option': {
    fontWeight: theme.typography.caption.fontWeight,
  },
  '.ag-right-aligned-cell': {
    letterSpacing: theme.typography.caption.letterSpacing,
  },
  '.ag-picker-field-wrapper': {
    borderRadius: theme.shape.borderRadius,
  },
  '.ag-compact-menu-option-active, .ag-menu-option-active': {
    backgroundColor: themeVars(theme).palette.action.hover,
  },
  '.ag-status-bar': {
    paddingLeft: 'calc(var(--ag-spacing)* 1)',
    paddingRight: 'calc(var(--ag-spacing)* 1)',
  },

  // Style AgGrid inputs to look like MDS TextField
  [agInputFieldSelector]: {
    border: 0,
    boxShadow: 'none',
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: '1px solid',
    borderBottomColor: themeVars(theme).palette.border.subtle,
    transition: theme.transitions.create(['border-bottom-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      borderBottomColor: themeVars(theme).palette.border.strong,
    },
    '&:focus': {
      borderBottomColor: themeVars(theme).palette.border.focused,
    },
  },

  // In cells, AgGrid inputs will look slightly different
  '.ag-cell': {
    [agInputFieldSelector]: {
      backgroundColor: themeVars(theme).palette.background.level2,
      '&::selection': {
        backgroundColor: themeVars(theme).palette.primary.main,
        color: themeVars(theme).palette.primary.contrastText,
      },
    },
  },

  '.ag-cell-inline-editing': {
    borderRadius: 0,
  },

  [agFocusedCellSelector]: {
    borderWidth: '1.5px',
  },

  '.ag-cell.ag-cell-range-selected:not(.ag-cell-range-single-cell)': {
    '&.ag-cell-range-top, &.ag-cell-range-left, &.ag-cell-range-right, &.ag-cell-range-bottom': {
      borderWidth: '1.5px',
    },
  },

  // Extra class utilities exposed to @mds/ag-grid consumers
  [`.${gridClasses.cellNumeric}`]: {
    fontVariantNumeric: 'tabular-nums',
  },
  [`.${gridClasses.cellNoPadding}`]: {
    paddingInline: 0,
  },
  [`.${gridClasses.cellPositive}:not(.ag-cell-range-selected)`]: {
    background: `rgba(${themeVars(theme).palette.success.mainChannel} / 0.1)`,
  },
  [`.${gridClasses.cellNegative}:not(.ag-cell-range-selected)`]: {
    background: `rgba(${themeVars(theme).palette.error.mainChannel} / 0.1)`,
  },
  [`.${gridClasses.cellWarning}:not(.ag-cell-range-selected)`]: {
    background: `rgba(${themeVars(theme).palette.warning.mainChannel} / 0.1)`,
  },
  [`.${gridClasses.callInfo}:not(.ag-cell-range-selected)`]: {
    background: `rgba(${themeVars(theme).palette.info.mainChannel} / 0.1)`,
  },
  [`.${gridClasses.headerCellCentered} .ag-header-cell-label`]: {
    justifyContent: 'center',
  },
}));

export interface AgGridThemeProps {
  density?: 'regular' | 'compact' | 'spacious';
  columnBorders?: boolean;
}

/**
 * Color scheme aware Marex AgGrid theme
 */
export const useAgGridTheme = ({ density = 'regular', columnBorders }: AgGridThemeProps = {}) => {
  const { colorScheme } = useColorScheme();

  const theme = useMemo(() => {
    let result = colorScheme === 'dark' ? mdsThemeDark : mdsThemeLight;
    if (density === 'compact') {
      result = result.withParams(compactDensityParams);
    } else if (density === 'spacious') {
      result = result.withParams(spaciousDensityParams);
    }
    if (columnBorders) {
      result = result.withParams({
        columnBorder: { style: 'solid', color: colorScheme === 'dark' ? dark.border.subtle : light.border.subtle },
      });
    }
    return result;
  }, [colorScheme, columnBorders, density]);

  const chartThemes = useMemo(() => (colorScheme === 'dark' ? ['ag-default-dark'] : ['ag-default']), [colorScheme]);

  return { theme, chartThemes };
};
